import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';

const BASE_URL = '/api';
const getToken = () => localStorage.getItem('ozon_token');

async function apiFetch(path) {
  const r = await fetch(`${BASE_URL}${path}`, {
    headers: { Authorization: `Bearer ${getToken()}` },
  });
  if (!r.ok) throw new Error(await r.text());
  return r.json();
}

// ─── Yardımcı ─────────────────────────────────────────────────────────────────

function fmtDt(str) {
  if (!str) return '—';
  const d = new Date(str);
  return d.toLocaleString('tr-TR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit', second:'2-digit' });
}

const today = new Date().toISOString().slice(0, 10);

// ─── Sipariş Aktiviteleri Tab ──────────────────────────────────────────────────

const STATUS_LABELS = {
  awaiting_packaging:    'Paket Bekliyor',
  awaiting_registration: 'Kayıt Bekliyor',
  ready_to_ship:         'Sevkiyata Hazır',
  awaiting_deliver:      'Teslim Bekliyor',
  awaiting_supply:       'Tedarik Bekliyor',
  kargo_hazir:           'Kargo Hazır',
  delivering:            'Dağıtımda',
  delivered:             'Teslim Edildi',
  cancelled:             'İptal',
};

const EVENT_META = {
  order_created:    { icon: '🆕', label: 'Yeni Sipariş',      color: 'text-emerald-700 bg-emerald-50 border-emerald-200' },
  status_changed:   { icon: '🔄', label: 'Statü Değişimi',    color: 'text-blue-700 bg-blue-50 border-blue-200' },
  label_printed:    { icon: '🏷️', label: 'Etiket Basıldı',    color: 'text-violet-700 bg-violet-50 border-violet-200' },
  label_assigned:   { icon: '📌', label: 'Etiket Atandı',     color: 'text-amber-700 bg-amber-50 border-amber-200' },
  label_unassigned: { icon: '📌', label: 'Etiket Kaldırıldı', color: 'text-gray-600 bg-gray-50 border-gray-200' },
  note_added:       { icon: '📝', label: 'Not Eklendi',        color: 'text-teal-700 bg-teal-50 border-teal-200' },
};

const STATUS_OPTIONS = [
  { value: '', label: 'Tüm Statüler' },
  { value: 'awaiting_packaging',    label: 'Paket Bekliyor' },
  { value: 'awaiting_registration', label: 'Kayıt Bekliyor' },
  { value: 'ready_to_ship',         label: 'Sevkiyata Hazır' },
  { value: 'awaiting_deliver',      label: 'Teslim Bekliyor' },
  { value: 'awaiting_supply',       label: 'Tedarik Bekliyor' },
  { value: 'delivering',            label: 'Dağıtımda' },
  { value: 'delivered',             label: 'Teslim Edildi' },
  { value: 'cancelled',             label: 'İptal' },
];

const ORDER_PAGE_SIZE = 100;

function StatusBadge({ status }) {
  const map = {
    awaiting_packaging:    'bg-yellow-100 text-yellow-700',
    awaiting_registration: 'bg-orange-100 text-orange-700',
    ready_to_ship:         'bg-blue-100 text-blue-700',
    awaiting_deliver:      'bg-indigo-100 text-indigo-700',
    awaiting_supply:       'bg-rose-100 text-rose-700',
    kargo_hazir:           'bg-purple-100 text-purple-700',
    delivering:            'bg-violet-100 text-violet-700',
    delivered:             'bg-green-100 text-green-700',
    cancelled:             'bg-red-100 text-red-700',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function ActorBadge({ actor }) {
  if (!actor) return <span className="text-xs text-gray-300">—</span>;
  const isSystem = actor === 'ozon-sync' || actor === 'system';
  const isIncoming = actor.startsWith('incoming:');
  if (isIncoming) {
    const source = actor.replace('incoming:', '');
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium bg-purple-50 text-purple-700 border border-purple-100">
        📩 Gelen · {source}
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium ${
      isSystem ? 'bg-gray-100 text-gray-500' : 'bg-blue-50 text-blue-700'
    }`}>
      {isSystem ? '🤖' : '👤'} {actor}
    </span>
  );
}

function OrderActivities() {
  const [filters, setFilters] = useState({
    event_type: '', actor: '', posting_number: '', to_status: '', from_status: '',
    date_from: today, date_to: today,
  });
  const [page,    setPage]    = useState(0);
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('limit', ORDER_PAGE_SIZE);
      params.set('offset', page * ORDER_PAGE_SIZE);
      const data = await apiFetch(`/orders/activity-log?${params}`);
      setRows(data.rows); setTotal(data.total);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  function handleFilterChange(key, val) { setPage(0); setFilters(f => ({ ...f, [key]: val })); }
  function clearFilters() {
    setPage(0);
    setFilters({ event_type: '', actor: '', posting_number: '', to_status: '', from_status: '', date_from: today, date_to: today });
  }

  const totalPages = Math.ceil(total / ORDER_PAGE_SIZE);

  return (
    <>
      {/* Filtre Kartı */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 space-y-3">
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Tarih:</span>
            <input type="date" value={filters.date_from} onChange={e => handleFilterChange('date_from', e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
            <span className="text-gray-400 text-xs">—</span>
            <input type="date" value={filters.date_to} onChange={e => handleFilterChange('date_to', e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
          </div>
          <select value={filters.event_type} onChange={e => handleFilterChange('event_type', e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300">
            <option value="">Tüm Olaylar</option>
            <option value="status_changed">🔄 Statü Değişimi</option>
            <option value="label_printed">🏷️ Etiket Basıldı</option>
            <option value="order_created">🆕 Yeni Sipariş</option>
            <option value="label_assigned">📌 Etiket Atandı</option>
          </select>
          <select value={filters.to_status} onChange={e => handleFilterChange('to_status', e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300">
            <option value="">Hedef Statü (Hepsi)</option>
            {STATUS_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select value={filters.from_status} onChange={e => handleFilterChange('from_status', e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300">
            <option value="">Kaynak Statü (Hepsi)</option>
            {STATUS_OPTIONS.filter(o => o.value).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <input type="text" placeholder="Sipariş no ara..." value={filters.posting_number}
            onChange={e => handleFilterChange('posting_number', e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-blue-300" />
          <input type="text" placeholder="Kullanıcı / aktör..." value={filters.actor}
            onChange={e => handleFilterChange('actor', e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-44 focus:outline-none focus:ring-2 focus:ring-blue-300" />
          <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2">Temizle</button>
          <button onClick={() => { setPage(0); setFilters(f => ({ ...f, event_type: 'status_changed', to_status: 'awaiting_deliver' })); }}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-indigo-50 border border-indigo-200 text-indigo-700 rounded-lg hover:bg-indigo-100">
            🎯 Teslime Gönderilenleri Göster
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
          <span className="text-sm text-gray-500">
            {loading ? 'Yükleniyor...' : `${total} kayıt`}
            {totalPages > 1 && <span className="text-gray-300 ml-2">· Sayfa {page + 1}/{totalPages}</span>}
          </span>
          <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40">↻ Yenile</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[700px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-40">Zaman</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-40">Sipariş No</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-32">Olay</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Açıklama / Detay</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Aktör</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Güncel Statü</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && (
                <tr><td colSpan={6} className="text-center py-16 text-gray-400">
                  <div className="inline-block w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mb-2" />
                  <div>Yükleniyor...</div>
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={6} className="text-center py-16 text-gray-400">Filtreye uyan kayıt bulunamadı.</td></tr>
              )}
              {!loading && rows.map(row => {
                const meta = row.meta || {};
                const evMeta = EVENT_META[row.event_type] || { icon: '•', label: row.event_type, color: 'text-gray-600 bg-gray-50 border-gray-200' };
                const isStatusChange = row.event_type === 'status_changed';
                const toAwaiting = meta.to === 'awaiting_deliver';
                return (
                  <tr key={row.id} className={`transition-colors hover:bg-gray-50/60 ${toAwaiting ? 'bg-indigo-50/30' : ''}`}>
                    <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">{fmtDt(row.created_at)}</td>
                    <td className="px-4 py-3">
                      <Link to={`/orders/${row.posting_number}`} className="font-mono text-xs text-blue-600 hover:underline">{row.posting_number}</Link>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border ${evMeta.color}`}>
                        {evMeta.icon} {evMeta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-xs text-gray-700">{row.description}</div>
                      {isStatusChange && meta.from && meta.to && (
                        <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                          <StatusBadge status={meta.from} />
                          <span className="text-gray-300 text-xs">→</span>
                          <StatusBadge status={meta.to} />
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3"><ActorBadge actor={row.actor} /></td>
                    <td className="px-4 py-3">
                      {row.current_status ? <StatusBadge status={row.current_status} /> : <span className="text-xs text-gray-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-100">
            <button onClick={() => setPage(0)} disabled={page === 0}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">««</button>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">‹ Önceki</button>
            <span className="text-xs text-gray-500 px-3">{page + 1} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">Sonraki ›</button>
            <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">»»</button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Stok Aktiviteleri Tab ─────────────────────────────────────────────────────

const STOCK_PAGE_SIZE = 100;

function StockActivities() {
  const [filters, setFilters] = useState({ offer_id: '', date_from: today, date_to: today });
  const [page,    setPage]    = useState(0);
  const [rows,    setRows]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
      params.set('limit', STOCK_PAGE_SIZE);
      params.set('offset', page * STOCK_PAGE_SIZE);
      const data = await apiFetch(`/ozon-stock/logs?${params}`);
      setRows(data.rows); setTotal(data.total);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [filters, page]);

  useEffect(() => { load(); }, [load]);

  function handleFilterChange(key, val) { setPage(0); setFilters(f => ({ ...f, [key]: val })); }
  function clearFilters() {
    setPage(0);
    setFilters({ offer_id: '', date_from: today, date_to: today });
  }

  const totalPages = Math.ceil(total / STOCK_PAGE_SIZE);

  function stockDiff(oldVal, newVal) {
    if (oldVal == null) return null;
    const diff = newVal - oldVal;
    if (diff === 0) return <span className="text-gray-400 text-xs">±0</span>;
    if (diff > 0)   return <span className="text-green-600 text-xs font-semibold">+{diff}</span>;
    return <span className="text-red-500 text-xs font-semibold">{diff}</span>;
  }

  return (
    <>
      {/* Filtre */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-5 space-y-3">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 font-medium">Tarih:</span>
            <input type="date" value={filters.date_from} onChange={e => handleFilterChange('date_from', e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
            <span className="text-gray-400 text-xs">—</span>
            <input type="date" value={filters.date_to} onChange={e => handleFilterChange('date_to', e.target.value)}
              className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
          </div>
          <input type="text" placeholder="Offer ID ara..." value={filters.offer_id}
            onChange={e => handleFilterChange('offer_id', e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm w-52 focus:outline-none focus:ring-2 focus:ring-blue-300" />
          <button onClick={clearFilters} className="text-xs text-gray-400 hover:text-gray-600 underline underline-offset-2">Temizle</button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/60">
          <span className="text-sm text-gray-500">
            {loading ? 'Yükleniyor...' : `${total} kayıt`}
            {totalPages > 1 && <span className="text-gray-300 ml-2">· Sayfa {page + 1}/{totalPages}</span>}
          </span>
          <button onClick={load} disabled={loading} className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40">↻ Yenile</button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-40">Zaman</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Offer ID / Ürün</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-20">Tip</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Önceki</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-24">Yeni</th>
                <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-20">Fark</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Yapan</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && (
                <tr><td colSpan={7} className="text-center py-16 text-gray-400">
                  <div className="inline-block w-5 h-5 border-2 border-blue-400 border-t-transparent rounded-full animate-spin mb-2" />
                  <div>Yükleniyor...</div>
                </td></tr>
              )}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={7} className="text-center py-16 text-gray-400">Filtreye uyan kayıt bulunamadı.</td></tr>
              )}
              {!loading && rows.map(row => (
                <tr key={row.id} className="hover:bg-gray-50/60 transition-colors">
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">{fmtDt(row.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs text-gray-800">{row.offer_id}</div>
                    {row.name && <div className="text-[11px] text-gray-400 truncate max-w-[240px]">{row.name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      {row.type?.toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center text-xs text-gray-500">
                    {row.old_stock != null ? row.old_stock : <span className="text-gray-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center text-sm font-bold text-gray-800">{row.new_stock}</td>
                  <td className="px-4 py-3 text-center">{stockDiff(row.old_stock, row.new_stock)}</td>
                  <td className="px-4 py-3">
                    <ActorBadge actor={row.changed_by} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-100">
            <button onClick={() => setPage(0)} disabled={page === 0}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">««</button>
            <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">‹ Önceki</button>
            <span className="text-xs text-gray-500 px-3">{page + 1} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">Sonraki ›</button>
            <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}
              className="px-3 py-1 rounded-lg text-xs font-medium border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40">»»</button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── Ana Sayfa ─────────────────────────────────────────────────────────────────

const TABS = [
  { key: 'orders', label: 'Sipariş Aktiviteleri' },
  { key: 'stock',  label: 'Stok Aktiviteleri' },
];

export default function ActivityLog() {
  const [activeTab, setActiveTab] = useState('orders');

  return (
    <div className="p-3 sm:p-5 md:p-6 max-w-screen-xl mx-auto">
      {/* Başlık */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900">Aktivite Geçmişi</h1>
        <p className="text-sm text-gray-500 mt-1">Tüm sipariş ve stok hareketleri</p>
      </div>

      {/* Tab Bar */}
      <div className="flex gap-1 mb-6 border-b border-gray-200">
        {TABS.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`px-5 py-2.5 text-sm font-medium rounded-t-lg transition-colors border-b-2 -mb-px ${
              activeTab === tab.key
                ? 'border-blue-600 text-blue-700 bg-blue-50'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}>
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'orders' ? <OrderActivities /> : <StockActivities />}
    </div>
  );
}
